import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Plus, Minus } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import { useLanguage } from '../i18n/LanguageContext';
import { faqContent } from '../i18n/content/faq';

const WhatsAppLink = () => (
  <a href="https://wa.me/32460201577" target="_blank" rel="noopener noreferrer" className='text-primary hover:text-primary-dark font-medium' style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M20.52 3.48A11.77 11.77 0 0 0 12.04 0C5.39 0 .02 5.37.02 12.02c0 2.12.55 4.18 1.59 6.01L0 24l6.14-1.61a11.93 11.93 0 0 0 5.9 1.51h.01c6.64 0 12.02-5.38 12.02-12.02 0-3.21-1.25-6.22-3.55-8.4zM12.05 21.5c-1.88 0-3.71-.5-5.3-1.44l-.38-.23-3.64.96.97-3.54-.25-.37a9.48 9.48 0 0 1-1.46-5.1c0-5.24 4.27-9.51 9.52-9.51 2.54 0 4.92.99 6.72 2.78a9.43 9.43 0 0 1 2.78 6.72c0 5.25-4.27 9.53-9.52 9.53zm5.26-7.12c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.65.15-.19.29-.75.95-.92 1.14-.17.19-.34.21-.63.06-.29-.15-1.23-.45-2.35-1.44-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.14-.14.29-.34.43-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.65-1.57-.89-2.15-.23-.56-.47-.49-.65-.5h-.56c-.19 0-.51.07-.77.36-.26.29-1.01.98-1.01 2.39s1.03 2.77 1.17 2.96c.14.19 2.02 3.09 4.89 4.33.68.29 1.21.46 1.62.59.68.21 1.29.18 1.77.11.54-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.54-.33z" />
    </svg>
    +32 460 20 15 77
  </a>
);

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(0);
  const { lang } = useLanguage();
  const c = faqContent[lang];
  const faqData = c.items.map((item) => ({
    question: item.question,
    answer: item.hasLink ? (
      <>
        {item.answer}{" "}
        <WhatsAppLink />
      </>
    ) : item.answer,
  }));

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="section2" id="faq" style={{backgroundColor: 'var(--bg-color)'}}>
      <div className="main-content">
        <div className="container mt-5 pb-10 pt-2">
          <Reveal className="mb-10">
            <SectionHeading eyebrow={c.eyebrow} title={c.title} />
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 max-w-full mx-auto">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                className="bg-surface rounded-card-md shadow-sm border border-line p-6 cursor-pointer transition-all duration-300 hover:border-primary/40"
                onClick={() => toggleFaq(index)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-ink text-lg pr-4">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {openIndex === index ? (
                      <Minus className="w-6 h-6 text-primary" />
                    ) : (
                      <Plus className="w-6 h-6 text-primary" />
                    )}
                  </div>
                </div>

                <AnimatePresence mode="sync">
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: 'easeInOut',
                      }}
                      className="overflow-hidden"
                    >
                      <p className="text-ink-muted mt-4 pt-4 border-t border-line leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
